"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import useCookieConsent from "@/hooks/useCookieConsent";

/**
 * Pose le cookie d'attribution d'un lien affilié (?ref=).
 *
 * Ce cookie sert à rémunérer un partenaire : il relève de l'accord
 * « publicité » (lib/cookieConsent.js). Si le visiteur n'a pas encore choisi,
 * le code affilié attend en mémoire, et le cookie est posé s'il accepte
 * pendant la même visite. Le clic, lui, est compté côté serveur sans rien
 * déposer, quel que soit le choix.
 */
export default function ReferralTracker() {
    const searchParams = useSearchParams();
    const consent = useCookieConsent();
    const pending = useRef(null);

    useEffect(() => {
        const ref = searchParams.get("ref");
        if (!ref) return;

        const tag = searchParams.get("tag");
        pending.current = { ref, tag };

        // Track click (fire-and-forget)
        fetch("/api/affiliate/post/track-click", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ referralCode: ref, ...(tag && { tag }) }),
        }).catch(() => {});

        // Clean the URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete("ref");
        url.searchParams.delete("tag");
        window.history.replaceState({}, "", url.toString());
    }, [searchParams]);

    useEffect(() => {
        if (!pending.current || consent?.marketing !== true) return;
        const { ref, tag } = pending.current;
        pending.current = null;

        // Set the referral cookie (30 days)
        const maxAge = 30 * 24 * 60 * 60;
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `blvmy_ref=${encodeURIComponent(ref)}; max-age=${maxAge}; path=/; SameSite=Lax${secure}`;

        // Set the tag cookie if present (separate cookie, same expiry)
        if (tag) {
            document.cookie = `blvmy_ref_tag=${encodeURIComponent(tag)}; max-age=${maxAge}; path=/; SameSite=Lax${secure}`;
        }
    }, [consent, searchParams]);

    return null;
}
